/**
 * MODULE: domain/sales-target-management/frontend/hooks/useSalesTargets
 * PURPOSE: React Query hooks for sales targets CRUD operations
 * NAMING: Sales Targets = revenue quotas (NOT platform usage quotas)
 * RECENT: 2026-05-07 — **`ErrorWithCause`** cast for **`cause`** walk (shell **`tsc --noEmit`** without **`lib` ES2022**).
 * RECENT_PRIOR: 2026-05-03 [2.2.794] — **`refetchOnWindowFocus: false`** on queries; broader **`shouldRetrySalesTargetsQuery`** so DNS or network failures never retry (quiet console).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { triggerQuotaPlanningLearningRefresh } from '@shared/lib/frontend/utils/salesTargetSetBridge';
import { salesTargetsApi } from '../api/sales-targets-client';
import type {
  SalesTarget,
  SalesTargetCreate,
  SalesTargetUpdate,
  SalesTargetPerformance,
  TeamSalesTargetRollup,
} from '../../contracts/sales-targets.types';

type ErrorWithCause = Error & { cause?: unknown };

/** Flatten **`Error.cause`** chains for substring checks (DNS / offline / poisoned host in message). */
function salesTargetsErrorText(error: unknown): string {
  const parts: string[] = [];
  let e: unknown = error;
  for (let i = 0; i < 6 && e != null; i++) {
    if (e instanceof Error) {
      const withCause = e as ErrorWithCause;
      parts.push(e.message, String(withCause.cause ?? ''));
      e = withCause.cause;
    } else {
      parts.push(String(e));
      break;
    }
  }
  return parts.join(' ').toLowerCase();
}

/**
 * React Query retry guard. `sales-targets-client` uses fetch and attaches **`status`** on errors
 * (not `response.status` like Axios) so 401/403/429 must be read from **`error.status`**.
 * Never retries after the first failure when the error looks like transport or DNS (stops console floods).
 */
export function shouldRetrySalesTargetsQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  const blob = salesTargetsErrorText(error);
  if (
    blob.includes('failed to fetch') ||
    blob.includes('networkerror') ||
    blob.includes('err_name_not_resolved') ||
    blob.includes('load failed') ||
    blob.includes('network request failed') ||
    blob.includes('brazn-')
  ) {
    return false;
  }
  if (!error || typeof error !== 'object') return false;
  const e = error as { status?: number; code?: string; message?: string; response?: { status?: number } };
  const status = e.status ?? e.response?.status;
  if (status === 429 || status === 401 || status === 403) return false;
  if (e.code === 'ERR_NETWORK' || e.code === 'ERR_NAME_NOT_RESOLVED') return false;
  const msg = String(e.message ?? '');
  if (msg.includes('Failed to fetch') || msg.includes('Network Error')) return false;
  return false;
}

const ONBOARDING_QUERY_KEY = ['onboarding'] as const;

function refreshQuotaAssistantContext(queryClient: ReturnType<typeof useQueryClient>) {
  triggerQuotaPlanningLearningRefresh();
  void queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
}

const QUERY_KEYS = {
  targets: ['sales-targets'] as const,
  target: (id: string) => ['sales-targets', id] as const,
  progress: (id: string) => ['sales-targets', id, 'progress'] as const,
  performanceList: ['sales-targets', 'performance'] as const,
  teamRollup: ['sales-targets', 'team-rollup'] as const,
};

export function useSalesTargets(filters?: {
  user_id?: string;
  territory_id?: string;
  fiscal_year?: number;
  fiscal_quarter?: number;
  period_type?: 'monthly' | 'quarterly' | 'yearly';
  status?: string;
}) {
  const queryClient = useQueryClient();

  const {
    data: targets,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [...QUERY_KEYS.targets, filters],
    queryFn: () => salesTargetsApi.listTargets(filters),
    staleTime: 60_000,
    retry: shouldRetrySalesTargetsQuery,
    refetchOnWindowFocus: false,
  });

  const createTarget = useMutation({
    mutationFn: (data: SalesTargetCreate) => salesTargetsApi.createTarget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.targets });
      refreshQuotaAssistantContext(queryClient);
    },
  });

  const updateTarget = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SalesTargetUpdate }) =>
      salesTargetsApi.updateTarget(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.targets });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.target(variables.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.progress(variables.id) });
      refreshQuotaAssistantContext(queryClient);
    },
  });

  const deleteTarget = useMutation({
    mutationFn: (id: string) => salesTargetsApi.deleteTarget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.targets });
      refreshQuotaAssistantContext(queryClient);
    },
  });

  return {
    targets,
    isLoading,
    error,
    refetch,
    createTarget,
    updateTarget,
    deleteTarget,
  };
}

export function useSalesTarget(id: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.target(id!),
    queryFn: () => salesTargetsApi.getTarget(id!),
    enabled: !!id,
    staleTime: 30_000,
    retry: shouldRetrySalesTargetsQuery,
    refetchOnWindowFocus: false,
  });
}

export function useSalesTargetProgress(id: string | undefined) {
  return useQuery({
    queryKey: QUERY_KEYS.progress(id!),
    queryFn: () => salesTargetsApi.getProgress(id!),
    enabled: !!id,
    staleTime: 30_000,
    retry: shouldRetrySalesTargetsQuery,
    refetchOnWindowFocus: false,
  });
}

export function useSalesTargetPerformanceList(filters?: {
  user_id?: string;
  territory_id?: string;
  fiscal_year?: number;
  health_status?: string;
}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.performanceList, filters],
    queryFn: () => salesTargetsApi.listPerformance(filters),
    staleTime: 60_000,
    retry: shouldRetrySalesTargetsQuery,
    refetchOnWindowFocus: false,
  });
}

export function useTeamRollup(manager_id?: string) {
  return useQuery({
    queryKey: [...QUERY_KEYS.teamRollup, manager_id],
    queryFn: () => salesTargetsApi.listTeamRollup(manager_id),
    staleTime: 60_000,
    retry: shouldRetrySalesTargetsQuery,
    refetchOnWindowFocus: false,
  });
}
